import { useEffect, useRef } from 'react';

/**
 * Ctrl+S on every platform, and Cmd+S too so Mac muscle memory lands. The
 * guide editor's shortcuts dialog advertises only Ctrl+S, matching the line
 * shortcuts next to it, which are Ctrl-only on Mac as well.
 *
 * Unlike the guide editor's arrow shortcuts this deliberately fires inside
 * text inputs, since saving mid-sentence is the whole point. It swallows the
 * chord even when onSave declines to act, so a no-op save still doesn't drop
 * the browser's Save Page dialog on someone mid-edit.
 */
export function useSaveShortcut(onSave: () => void): void {
   // Keeps the listener stable while onSave stays fresh; StepEditor re-renders
   // on every keystroke, so re-binding per render would churn.
   const onSaveRef = useRef(onSave);
   useEffect(() => {
      onSaveRef.current = onSave;
   }, [onSave]);

   useEffect(() => {
      const onKeyDown = (event: KeyboardEvent) => {
         if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
            return;
         }
         if (event.key.toLowerCase() !== 's') {
            return;
         }
         event.preventDefault();
         onSaveRef.current();
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
   }, []);
}
